/**
 * Draft Conflict Detection Hook
 * Detects and manages conflicts between local and server drafts
 */

import { useState, useCallback, useRef } from 'react';
import {
  detectConflict,
  mergeDrafts,
  hashDraftAnswers,
  ConflictResult,
  DraftVersion,
  MergeStrategy,
} from '@/services/draftConflictService';

export interface UseDraftConflictDetectionOptions {
  onResolve?: (resolvedAnswers: Record<string, any>) => void;
}

export interface UseDraftConflictDetectionReturn {
  conflict: ConflictResult | null;
  isConflictDialogOpen: boolean;
  checkForConflict: (
    localAnswers: Record<string, any>,
    serverAnswers: Record<string, any>,
    serverUpdatedAt: string
  ) => boolean;
  resolveConflict: (strategy: MergeStrategy) => void;
  dismissConflict: () => void;
  openConflictDialog: () => void;
}

export function useDraftConflictDetection(
  options: UseDraftConflictDetectionOptions = {}
): UseDraftConflictDetectionReturn {
  const { onResolve } = options;
  const [conflict, setConflict] = useState<ConflictResult | null>(null);
  const [isConflictDialogOpen, setIsConflictDialogOpen] = useState(false);
  const lastLocalHashRef = useRef<string>('');
  const lastLocalTimeRef = useRef<string>(new Date().toISOString());

  const checkForConflict = useCallback(
    (
      localAnswers: Record<string, any>,
      serverAnswers: Record<string, any>,
      serverUpdatedAt: string
    ): boolean => {
      // Store current local state for comparison
      const localHash = hashDraftAnswers(localAnswers);
      const localUpdatedAt = lastLocalTimeRef.current;
      
      // Only check if we have a previous local hash
      if (!lastLocalHashRef.current) {
        lastLocalHashRef.current = localHash;
        return false;
      }
      
      // Skip if local hash hasn't changed
      if (localHash === lastLocalHashRef.current) {
        return false;
      }
      
      // Detect conflict
      const result = detectConflict(
        localAnswers,
        localUpdatedAt,
        serverAnswers,
        serverUpdatedAt
      );
      
      if (result.hasConflict) {
        setConflict(result);
        setIsConflictDialogOpen(true);
        return true;
      }
      
      // Update refs for next check
      lastLocalHashRef.current = localHash;
      lastLocalTimeRef.current = new Date().toISOString();
      
      return false;
    },
    []
  );

  const resolveConflict = useCallback(
    (strategy: MergeStrategy) => {
      if (!conflict?.localVersion || !conflict?.serverVersion) return;
      
      const resolved = mergeDrafts(conflict.localVersion, conflict.serverVersion, strategy);
      
      // Update refs with resolved state
      lastLocalHashRef.current = hashDraftAnswers(resolved);
      lastLocalTimeRef.current = new Date().toISOString();
      
      // Clear conflict
      setConflict(null);
      setIsConflictDialogOpen(false);
      
      // Notify parent
      onResolve?.(resolved);
    },
    [conflict, onResolve]
  );

  const dismissConflict = useCallback(() => {
    setIsConflictDialogOpen(false);
  }, []);

  const openConflictDialog = useCallback(() => {
    if (conflict) {
      setIsConflictDialogOpen(true);
    }
  }, [conflict]);

  return {
    conflict,
    isConflictDialogOpen,
    checkForConflict,
    resolveConflict,
    dismissConflict,
    openConflictDialog,
  };
}
